import { useMutation } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { subscribeAccount } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { connectionErrorMessage } from './error-message';
import forms from './forms.module.css';

export const SUBSCRIBED_TEXT =
  'Conta inscrita no aplicativo. As mensagens novas passam a chegar ao CladvApp.';
export const SUBSCRIBE_FAILED_TEXT = 'Não foi possível inscrever a conta. Tente de novo.';

/**
 * Depois de verificar o webhook, o serviço (na Meta) ainda só entrega as mensagens de uma conta que
 * esteja inscrita no aplicativo. O botão faz essa inscrição com as credenciais já salvas; repetir
 * não faz mal. Só é montado quando a conexão diz que o provedor permite (`canSubscribe`).
 */
export function SubscribeAccount(): ReactElement {
  const status = useStatusMessage();
  const [error, setError] = useState<string | null>(null);
  const subscribe = useMutation({ mutationFn: subscribeAccount });

  const run = (): void => {
    if (subscribe.isPending) return;
    setError(null);
    status.announce('');
    subscribe.mutate(undefined, {
      onSuccess: () => status.announce(SUBSCRIBED_TEXT),
      onError: (failure) => setError(connectionErrorMessage(failure, SUBSCRIBE_FAILED_TEXT)),
    });
  };

  return (
    <>
      <p className={forms.hint}>
        Por último, inscreva a conta no aplicativo: sem isso o serviço não manda as mensagens para o
        endereço acima, mesmo com o webhook verificado. É seguro repetir.
      </p>
      <div className={forms.actions}>
        <Button busy={subscribe.isPending} onClick={run}>
          {subscribe.isPending ? 'Inscrevendo…' : 'Inscrever a conta no aplicativo'}
        </Button>
      </div>
      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={forms.alert}>
          {error}
        </p>
      )}
    </>
  );
}
